// src/pages/interview/InterviewSummaryPage.js
import { useState, useEffect, useRef } from "react";
import { useInterviewContext } from "../../contexts/InterviewContext";
import {
  Trophy,
  Star,
  TrendingUp,
  Download,
  RotateCcw,
  Home,
  FileText,
  AlertTriangle,
} from "lucide-react"; // Using lucide-react for icons
import Loader from "../../components/Loader";
import Markdown from "react-markdown";
import { downloadInterviewReport } from "../../utils/fileUtils";

const InterviewSummaryPage = ({
  onStartOver,
  onGoHome,
}) => {
  const {
    userResponses,
    questions,
    overallFeedbackText,
    isLoading,
    overallFeedbackError,
    resetInterview,
  } = useInterviewContext();

  const [animateStats, setAnimateStats] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true); // Keep confetti for now
  const overallFeedbackRef = useRef(null);
  const pageTitleRef = useRef(null);

  useEffect(() => {
    if(pageTitleRef.current) pageTitleRef.current.focus();

    const timer = setTimeout(() => setAnimateStats(true), 300);
    const confettiTimer = setTimeout(() => setShowConfetti(false), 5000); // Slightly longer confetti
    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, []);

  useEffect(() => {
    if ((overallFeedbackText || overallFeedbackError) && overallFeedbackRef.current) {
        overallFeedbackRef.current.focus();
    }
  }, [overallFeedbackText, overallFeedbackError]);


  const calculateMetrics = () => {
    if (!userResponses?.length)
      return {
        avgScore: 0,
        strongAreas: 0,
        improvementAreas: 0,
        totalAnswered: 0,
      };

    const validResponses = userResponses.filter(
      (r) => r.feedback && r.feedback.overallScore !== undefined
    );
    const scores = validResponses.map(
      (response) => response.feedback.overallScore
    );

    const avgScore = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : 0;
    const strongAreas = validResponses.filter(
      (r) => r.feedback.overallScore >= 80
    ).length;
    const improvementAreas = validResponses.filter(
      (r) => r.feedback.overallScore < 60
    ).length;
    return {
      avgScore,
      strongAreas,
      improvementAreas,
      totalAnswered: userResponses.length,
    };
  };

  const metrics = calculateMetrics();
  const totalQuestionsPlanned = questions?.length || 0;

  const getPerformanceMessage = (score) => {
    if (score >= 80)
      return {
        text: "Outstanding Performance!",
        color: "text-green-300", // Dark theme contrast
        icon: "🌟",
      };
    if (score >= 60)
      return { text: "Great Job!", color: "text-sky-300", icon: "🎯" }; // Dark theme contrast
    if (score > 0)
      return { text: "Good Effort!", color: "text-purple-300", icon: "💪" }; // Dark theme contrast
    return { text: "Practice Complete!", color: "text-slate-300", icon: "🏁" }; // Dark theme contrast
  };
  const performance = getPerformanceMessage(metrics.avgScore);

  const handleStartOverClick = () => {
    resetInterview();
    if (onStartOver) onStartOver();
  };

  const handleGoHomeClick = () => {
    resetInterview();
    if (onGoHome) onGoHome();
  };

  return (
    <main className="min-h-screen bg-slate-900 text-slate-100 relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {showConfetti && (
        <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-10">
          {[...Array(70)].map((_, i) => ( // Increased confetti count
              <div
                key={i}
                className="absolute animate-fall" // Using a simpler fall animation
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 4}s`,
                  animationDuration: `${4 + Math.random() * 4}s`,
                }}
              >
                <div  style={{
                    width: `${Math.random() * 4 + 3}px`,
                    height: `${Math.random() * 8 + 5}px`,
                    backgroundColor: `hsl(${Math.random() * 360}, 80%, 70%)`, // Brighter HSL for dark theme
                    opacity: Math.random() * 0.6 + 0.4,
                    transform: `rotate(${Math.random() * 360}deg)`
                }} />
              </div>
            )
          )}
        </div>
      )}
      {/* Removed subtle background pattern as it might be too busy with dark theme + confetti */}

      <div className="relative z-20 max-w-5xl mx-auto"> {/* Slightly wider max-width */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-sky-600 rounded-full mb-6 shadow-xl transform hover:scale-110 transition-transform duration-300 ring-4 ring-slate-700">
            <Trophy aria-hidden="true" className="w-12 h-12 text-white" />
          </div>
          <h1 ref={pageTitleRef} tabIndex={-1} className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-purple-400 to-sky-400 bg-clip-text text-transparent mb-4 outline-none">
            Interview Complete!
          </h1>
          <div className="flex items-center justify-center gap-2 mb-4">
            <span aria-hidden="true" className="text-3xl">{performance.icon}</span>
            <h2 className={`text-3xl font-semibold ${performance.color}`}>
              {performance.text}
            </h2>
          </div>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            You've successfully answered{" "}
            <span className="font-semibold text-sky-300">
              {metrics.totalAnswered}
            </span>{" "}
            out of{" "}
            <span className="font-semibold text-sky-300">
              {totalQuestionsPlanned}
            </span>{" "}
            planned questions. Here's your performance breakdown.
          </p>
        </div>

        <section aria-labelledby="metrics-summary-heading" className="grid md:grid-cols-3 gap-6 mb-12">
          <h2 id="metrics-summary-heading" className="sr-only">Performance Metrics</h2>
          <div
            className={`bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-700 transform transition-all duration-500 delay-100 hover:shadow-sky-500/20 hover:-translate-y-1 ${
              animateStats
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-sky-500">
                <Star aria-hidden="true" className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Average Score
              </h3>
              <div className="text-4xl font-bold text-sky-300 mb-2">
                {animateStats
                  ? metrics.avgScore > 0
                    ? `${metrics.avgScore}%`
                    : "N/A"
                  : "0%"}
              </div>
              <p className="text-sm text-slate-400">
                Based on evaluated answers
              </p>
            </div>
          </div>
          <div
            className={`bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-700 transform transition-all duration-500 delay-300 hover:shadow-green-500/20 hover:-translate-y-1 ${
              animateStats
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
                <TrendingUp aria-hidden="true" className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Strong Answers
              </h3>
              <div className="text-4xl font-bold text-green-300 mb-2">
                {animateStats ? metrics.strongAreas : 0}
              </div>
              <p className="text-sm text-slate-400">Answers scoring 80%+</p>
            </div>
          </div>
          <div
            className={`bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-700 transform transition-all duration-500 delay-500 hover:shadow-orange-500/20 hover:-translate-y-1 ${
              animateStats
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-orange-500">
                <TrendingUp aria-hidden="true" className="w-8 h-8 text-white transform -rotate-45 scale-y-[-1]" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">
                Focus Areas
              </h3>
              <div className="text-4xl font-bold text-orange-300 mb-2">
                {animateStats ? metrics.improvementAreas : 0}
              </div>
              <p className="text-sm text-slate-400">Answers scoring below 60%</p>
            </div>
          </div>
        </section>

        <section aria-labelledby="overall-feedback-heading" ref={overallFeedbackRef} tabIndex={-1} className="bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-700 mb-12 outline-none">
          <h2 id="overall-feedback-heading" className="text-2xl font-bold text-slate-100 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-cyan-700 rounded-lg flex items-center justify-center border border-teal-500">
              <FileText aria-hidden="true" className="w-5 h-5 text-white" />
            </div>
            Personalized Overall Feedback
          </h2>
          {isLoading.overallFeedback && (
            <div role="status" aria-live="polite" className="flex justify-center items-center py-8">
              <Loader message="Generating your overall performance summary..." />
            </div>
          )}
          {overallFeedbackError && !isLoading.overallFeedback && (
            <div role="alert" aria-live="assertive" className="bg-red-700/20 border-l-4 border-red-500 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle
                    aria-hidden="true"
                    className="h-5 w-5 text-red-300" // Adjusted color
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-200"> {/* Adjusted color */}
                    Could not load overall feedback: {overallFeedbackError}
                  </p>
                </div>
              </div>
            </div>
          )}
          {overallFeedbackText &&
            !isLoading.overallFeedback &&
            !overallFeedbackError && (
              <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none prose-invert
                              prose-headings:text-slate-100 prose-p:text-slate-300
                              prose-strong:text-slate-100 prose-ul:text-slate-300
                              prose-li:marker:text-sky-400 prose-a:text-sky-400 hover:prose-a:text-sky-300">
                <Markdown>{overallFeedbackText}</Markdown>
              </div>
            )}
          {!overallFeedbackText &&
            !isLoading.overallFeedback &&
            !overallFeedbackError && (
              <p className="text-slate-400 italic">
                No overall feedback summary available at this time.
              </p>
            )}
        </section>

        {userResponses && userResponses.length > 0 && (
          <section aria-labelledby="review-answers-heading" className="bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-700 mb-12">
            <h2 id="review-answers-heading" className="text-2xl font-bold text-slate-100 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-700 rounded-lg flex items-center justify-center border border-purple-500">
                <Star aria-hidden="true" className="w-5 h-5 text-white" />
              </div>
              Review Your Answers
            </h2>
            <div className="space-y-4">
              {userResponses.map((response, index) => (
                <details
                  key={index}
                  className="group bg-slate-700/50 rounded-lg p-4 border border-slate-600/70"
                >
                  <summary className="font-medium text-slate-200 cursor-pointer group-open:text-sky-300 list-none flex justify-between items-center">
                    <span>
                      Q{index + 1}:{" "}
                      {response.question?.question?.substring(0, 80)}
                      {response.question?.question?.length > 80 ? "..." : ""}
                    </span>
                    <div className="flex items-center">
                      {response.feedback?.overallScore !== undefined && (
                        <span
                          className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${
                            response.feedback.overallScore >= 80
                              ? "bg-green-700/50 text-green-200" // Dark theme badge
                              : response.feedback.overallScore >= 60
                              ? "bg-sky-700/50 text-sky-200"   // Dark theme badge
                              : "bg-orange-700/50 text-orange-200" // Dark theme badge
                          }`}
                        >
                          {response.feedback.overallScore}%
                        </span>
                      )}
                      <span className="ml-2 text-slate-400 group-hover:text-sky-300 transition-transform duration-200 group-open:rotate-90">▼</span>
                    </div>
                  </summary>
                  <div className="mt-3 pt-3 border-t border-slate-600 prose prose-sm max-w-none prose-invert prose-p:text-slate-300 prose-strong:text-slate-100">
                    <p>
                      <strong>Your Answer:</strong>{" "}
                      {response.answerText || <span className="italic text-slate-400">N/A</span>}
                    </p>
                    {response.feedback &&
                      typeof response.feedback.overallAssessment ===
                        "string" && (
                        <div className="mt-2 p-3 bg-slate-700/70 rounded">
                          <strong>Feedback:</strong>{" "}
                          {response.feedback.overallAssessment}
                        </div>
                      )}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleStartOverClick}
            className="group relative w-full sm:w-auto px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-500 hover:to-blue-500 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-purple-500/30 flex items-center justify-center gap-3 text-base"
          >
            <RotateCcw aria-hidden="true" className="w-5 h-5 group-hover:rotate-[-180deg] transition-transform duration-500" />{" "}
            Practice Again
          </button>
          <button
            onClick={() => downloadInterviewReport(userResponses, overallFeedbackText)}
            className="w-full sm:w-auto px-8 py-3 sm:py-4 bg-slate-700 text-slate-100 font-semibold rounded-xl border-2 border-slate-600 hover:bg-slate-600 hover:border-slate-500 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-slate-500/20 flex items-center justify-center gap-3 text-base"
          >
            <Download aria-hidden="true" className="w-5 h-5" /> Download Report
          </button>
          <button
            onClick={handleGoHomeClick}
            className="w-full sm:w-auto px-8 py-3 sm:py-4 bg-slate-600 text-slate-100 font-semibold rounded-xl hover:bg-slate-500 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-slate-400/20 flex items-center justify-center gap-3 text-base"
          >
            <Home aria-hidden="true" className="w-5 h-5" /> Back to Home
          </button>
        </div>

        <div className="text-center mt-12">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/70 rounded-2xl p-8 border border-slate-700 shadow-xl">
            <blockquote className="text-xl italic text-slate-300 mb-4">
              "The expert in anything was once a beginner."
            </blockquote>
            <cite className="text-slate-400 font-medium">— Helen Hayes</cite>
          </div>
        </div>
      </div>
    </main>
  );
};

export default InterviewSummaryPage;
